import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Dropdown, Input, message, Modal, QRCode, Result, Select, Space, Table, Tag, Typography } from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useUserInfo } from '../../contexts/UserInfoContext';
import { useSalePolicy } from '../../contexts/SalePolicyContext';
import { copyToClipboard } from '../../helpers/clipboard';
import { SaleLink, SalePage, SaleResource, saleRequest, saleShareUrl } from '../../services/saleLinks';
import SaleCreateModal from './SaleCreateModal';
import SaleResourceView from './SaleResourceView';
import { saleDate, saleError, saleStatus, useSaleText } from './saleUi';
import './saleLinks.css';

const SaleLinks: React.FC = () => {
  const text = useSaleText();
  const navigate = useNavigate();
  const { isAuthenticated, status: loginStatus } = useUserInfo();
  const { policy, error: policyError, refresh: refreshPolicy } = useSalePolicy();
  const [data, setData] = useState<SalePage<SaleLink>>({ data: [], total: 0, page: 1, pageSize: 20 });
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<SaleLink | undefined>();
  const [createOpen, setCreateOpen] = useState(false);
  const [qrLink, setQrLink] = useState<SaleLink | null>(null);
  const [preview, setPreview] = useState<SaleResource | null>(null);
  const request = useRef(0);

  const load = useCallback(async () => {
    if (!isAuthenticated) return;
    const current = ++request.current;
    setLoading(true); setError('');
    try {
      const links = await saleRequest<SalePage<SaleLink>>(`?${new URLSearchParams({ page: String(page), pageSize: '20', keyword, status })}`);
      if (request.current === current) setData(links);
    } catch (failure) { if (request.current === current) setError(saleError(failure, text)); }
    finally { if (request.current === current) setLoading(false); }
  }, [isAuthenticated, page, keyword, status, text]);
  const invalidateRequests = useCallback(() => { request.current++; }, []);
  useEffect(() => { void load(); return invalidateRequests; }, [load, invalidateRequests]);

  const updateStatus = (link: SaleLink, next: string) => {
    Modal.confirm({
      title: next === 'REVOKED' ? text('撤销此售卖链接？', 'Revoke this sale link?') : text('更新链接状态？', 'Update link status?'),
      content: next === 'REVOKED' ? text('此操作不能恢复。所有激活码和已有授权将无法再通过此入口访问；已取得的公开地址不受影响。', 'This cannot be undone. New and existing authorizations stop working at this entry; known public URLs remain accessible.')
        : text('暂停只影响新激活，已有授权仍可使用。', 'Pausing affects new activations only.'),
      okText: text('确认', 'Confirm'), cancelText: text('取消', 'Cancel'), okButtonProps: { danger: next === 'REVOKED' },
      onOk: async () => {
        try { await saleRequest(`/${link.id}/status`, 'POST', { status: next }); await load(); }
        catch (failure) { message.error(saleError(failure, text)); throw failure; }
      },
    });
  };

  if (!isAuthenticated && loginStatus !== 'loading') return <div className="sale-page"><Result status="info"
    title={text('登录后管理售卖链接', 'Sign in to manage sale links')} subTitle={text('登录后可查看创建权限和已有售卖记录。', 'Sign in to view your creation permissions and existing sales.')} /></div>;

  return <div className="sale-page">
    <Typography.Title level={2}>{text('售卖链接', 'Sale links')}</Typography.Title>
    <Typography.Paragraph type="secondary">{text('创建资源访问入口，批量交付激活码，查看激活和使用情况。', 'Create resource entries, distribute activation codes, and review usage.')}</Typography.Paragraph>
    {(error || policyError) && <Alert type="error" message={error || policyError?.message} showIcon />}
    {policy && !policy.creationEnabled && <Alert type="info" showIcon message={policy.sellerEnabled
      ? text('平台暂时关闭新建和批量发码，已有交付和售后仍可管理。', 'New sales and batches are currently closed. Existing delivery and support remain available.')
      : text('尚未开通新建和发码权限，请联系管理员。已有售卖记录仍可管理。', 'Ask an administrator to enable new sales and batches. Existing sales can still be managed.')} />}
    <Space wrap className="sale-toolbar">
      <Button type="primary" icon={<PlusOutlined />} disabled={!policy?.creationEnabled} onClick={() => { setEditing(undefined); setCreateOpen(true); }}>{text('创建售卖链接', 'Create sale link')}</Button>
      <Input.Search allowClear placeholder={text('搜索标题或备注', 'Search title or note')} onSearch={(value) => { setKeyword(value); setPage(1); }} />
      <Select value={status} style={{ minWidth: 150 }} onChange={(value) => { setStatus(value); setPage(1); }} options={[
        { value: '', label: text('全部状态', 'All statuses') }, ...['ACTIVE', 'PAUSED', 'REVOKED'].map((value) => ({ value, label: saleStatus(value, text) })),
      ]} />
      <Button icon={<ReloadOutlined />} onClick={() => void Promise.all([load(), refreshPolicy()]).catch(() => undefined)}>{text('刷新', 'Refresh')}</Button>
    </Space>
    <Card>
      <Table<SaleLink> rowKey="id" dataSource={data.data} loading={loading || loginStatus === 'loading'} scroll={{ x: 1000 }}
        pagination={{ current: page, pageSize: 20, total: data.total, showSizeChanger: false, onChange: setPage }} columns={[
          { title: text('资源', 'Resource'), dataIndex: 'title', width: 230, render: (_, link) => <Space direction="vertical" size={0}>
            <Typography.Text strong>{link.title}</Typography.Text><Typography.Text type="secondary">{link.fileName || text('原文件不可用', 'Source unavailable')}</Typography.Text>
            {link.remark && <Typography.Text type="secondary">{link.remark}</Typography.Text>}
          </Space> },
          { title: text('状态', 'Status'), dataIndex: 'status', render: (value) => <Tag color={value === 'ACTIVE' ? 'blue' : value === 'PAUSED' ? 'orange' : 'default'}>{saleStatus(value, text)}</Tag> },
          { title: text('激活码', 'Codes'), render: (_, link) => <Button type="link" onClick={() => navigate(`/sale-links/${link.id}/codes`)}>{link.activatedCount || 0} / {link.codeCount || 0}</Button> },
          { title: text('打开次数', 'Openings'), dataIndex: 'accessCount' },
          { title: text('最近打开', 'Last opened'), dataIndex: 'lastAccessTime', render: saleDate },
          { title: text('创建时间', 'Created'), dataIndex: 'createTime', render: saleDate },
          { title: text('操作', 'Actions'), fixed: 'right', width: 180, render: (_, link) => <Space>
            <Button type="link" onClick={() => copyToClipboard(saleShareUrl(link.linkCode, policy?.saleOrigin), text('链接已复制', 'Link copied'))}>{text('复制', 'Copy')}</Button>
            <Dropdown trigger={['click']} menu={{ items: [
              { key: 'codes', label: text('管理激活码', 'Manage codes'), onClick: () => navigate(`/sale-links/${link.id}/codes`) },
              { key: 'edit', label: text('编辑', 'Edit'), onClick: () => { setEditing(link); setCreateOpen(true); } },
              { key: 'qr', label: text('二维码', 'QR code'), onClick: () => setQrLink(link) },
              { key: 'preview', label: text('预览资源（不计次数）', 'Preview (not counted)'), onClick: () => {
                void saleRequest<SaleResource>(`/${link.id}/preview`).then(setPreview).catch((failure) => message.error(saleError(failure, text)));
              } },
              { key: 'pause', disabled: link.status === 'REVOKED', label: link.status === 'PAUSED' ? text('恢复新激活', 'Resume activations') : text('暂停新激活', 'Pause activations'),
                onClick: () => updateStatus(link, link.status === 'PAUSED' ? 'ACTIVE' : 'PAUSED') },
              { key: 'revoke', danger: true, disabled: link.status === 'REVOKED', label: text('撤销链接', 'Revoke link'), onClick: () => updateStatus(link, 'REVOKED') },
            ] }}><Button>{text('更多', 'More')}</Button></Dropdown>
          </Space> },
        ]} />
      <Typography.Text type="secondary">{text('激活码列为“曾激活 / 总生成”。打开次数统计售卖入口的成功授权，不等于销量或完整观看次数。', 'Codes show ever activated / generated. Openings count authorized entries, not sales or completed views.')}</Typography.Text>
    </Card>
    <SaleCreateModal open={createOpen} link={editing} onCancel={() => setCreateOpen(false)} onSaved={(link) => {
      setCreateOpen(false); if (!editing) navigate(`/sale-links/${link.id}/codes`); else void load();
    }} />
    <Modal open={!!qrLink} title={qrLink?.title} footer={null} onCancel={() => setQrLink(null)}>
      {qrLink && <Space direction="vertical"><QRCode value={saleShareUrl(qrLink.linkCode, policy?.saleOrigin)} /><Typography.Paragraph copyable>{saleShareUrl(qrLink.linkCode, policy?.saleOrigin)}</Typography.Paragraph></Space>}
    </Modal>
    <Modal open={!!preview} title={preview?.fileName} footer={null} width={1000} destroyOnClose onCancel={() => setPreview(null)}>
      {preview && <SaleResourceView resource={preview} />}
    </Modal>
  </div>;
};
export default SaleLinks;
